// ================================================================================================
// React
import * as React from "react";
import _ from "lodash";
import SwipeableViews from 'react-swipeable-views';

// Material UI components
import Checkbox from 'material-ui/Checkbox';
import MenuItem from 'material-ui/MenuItem';
import { Tabs, Tab } from 'material-ui/Tabs';
import TextField from 'material-ui/TextField';
import FlatButton from 'material-ui/FlatButton';
import IconButton from 'material-ui/IconButton';
import DropDownMenu from 'material-ui/DropDownMenu';
import { Toolbar, ToolbarGroup, ToolbarTitle } from 'material-ui/Toolbar';
import { Card, CardHeader, CardText } from 'material-ui/Card';
import { Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui/Table';

// Icons
import AddIcon from 'material-ui/svg-icons/content/add';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import RadioButtonChecked from 'material-ui/svg-icons/toggle/radio-button-checked';
import RadioButtonUnChecked from 'material-ui/svg-icons/toggle/radio-button-unchecked';

// Internal components
import { DebugEx } from "@Utils/DebugEx";
import { isDefined, isFunction } from "@Utils/Micro";
import { DialogConf, ExDialog } from '@Primitive/ExDialog';
import { Localization, LocalizationDefaultKey } from '@Primitive/Localization/Localization';
import { SortableTableRowStyle } from '@Primitive/SortableConst';
import { SVGIconSelector, YodiwoSVG } from '@UI/Icons/SvgIconsSelector';
import { ToolbarIconButton } from '@Primitive/ToolbarIconButton';
// ================================================================================================
export interface LocalizationManagementProps {
    width: number;
    height: number;
    NodeKey: string;
    PagesInfo: { [key: string]: { Name: string, key: string } };
    onCloseRequest: () => void;
    onSaveRequest: () => void;
    toolbarHeight: number;
}
// ------------------------------------------------------------------------------------------------
export interface LocalizationManagementState {
    tabIndex: number;
    dialogConf: DialogConf;
    selectedFlag: string;
    newLanguageCode: string;
    selectedLanguageCode: string;
}
// ------------------------------------------------------------------------------------------------
const TableCellCommonStyle: React.CSSProperties = {
    textAlign: 'center',
    paddingLeft: 8,
    paddingRight: 8
}
// ------------------------------------------------------------------------------------------------
export class LocalizationManagement extends React.Component<LocalizationManagementProps, LocalizationManagementState>{
    private localization: Localization;
    // -------------------------------------------------------------------------------------------
    constructor(props) {
        super(props);

        // Link with localization
        this.localization = _.cloneDeep(Localization.GetInstance(this.props.NodeKey));
        if (!isDefined(this.localization)) {
            DebugEx.TraceError("Failed to find the localization", this.props.NodeKey);
        }

        let defaultLang = this.localization.DefaultLanguage;
        let info = this.localization.GetSupportedLanguages();
        let otherLang = _.find(info, (i) => i.Code != defaultLang);

        this.state = {
            tabIndex: 0,
            dialogConf: null,
            selectedFlag: "",
            newLanguageCode: "",
            selectedLanguageCode: (otherLang) ? otherLang.Code : ""
        };
    }
    // -------------------------------------------------------------------------------------------
    renderLanguageTable() {
        let info = this.localization.GetSupportedLanguages();
        let count = 0;
        return <Table>
            <TableHeader
                displaySelectAll={false}
                adjustForCheckbox={false}
                enableSelectAll={false}>
                <TableRow>
                    <TableHeaderColumn
                        colSpan={1}
                        style={TableCellCommonStyle}>
                        Code
                        </TableHeaderColumn>
                    <TableHeaderColumn
                        colSpan={2}
                        style={TableCellCommonStyle}>
                        Label
                        </TableHeaderColumn>
                    <TableHeaderColumn
                        colSpan={1}
                        style={TableCellCommonStyle}>
                        Flag Icon
                        </TableHeaderColumn>
                    <TableHeaderColumn
                        colSpan={1}
                        style={TableCellCommonStyle}>
                        Enable
                        </TableHeaderColumn>
                    <TableHeaderColumn
                        colSpan={1}
                        style={TableCellCommonStyle}>
                        Default
                        </TableHeaderColumn>
                    <TableHeaderColumn
                        colSpan={1}
                        style={TableCellCommonStyle}>
                        Delete
                        </TableHeaderColumn>
                </TableRow>
            </TableHeader>
            <TableBody
                displayRowCheckbox={false}
                showRowHover={true}
                stripedRows={true}>
                {
                    _.map(info, (l, i: string) => {
                        count++;
                        return (
                            <TableRow
                                key={'table-row' + i}
                                style={SortableTableRowStyle}>
                                <TableRowColumn style={{ textAlign: "center" }} colSpan={1}>
                                    {l.Code}
                                </TableRowColumn>
                                <TableRowColumn colSpan={2}>
                                    <TextField
                                        id={"label-" + i}
                                        key={"label-" + i}
                                        fullWidth
                                        value={l.Label}
                                        onChange={(event) => {
                                            let value = (event.target as any).value;
                                            l.Label = value;
                                            this.localization.SetLanguageInfo(l);
                                            this.forceUpdate();
                                        }}
                                    />
                                </TableRowColumn>
                                <TableRowColumn colSpan={1}>
                                    <div
                                        style={{
                                            width: 35, height: 35,
                                            cursor: "pointer",
                                            margin: "0 auto"
                                        }}
                                        onClick={() => {
                                            this.setState({
                                                selectedFlag: l.FlagIcon,
                                                dialogConf: {
                                                    title: "Select Flag",
                                                    contentStaticSize: { w: 0.7, h: 0.9 },
                                                    actions: [
                                                        <FlatButton
                                                            label="Close"
                                                            secondary={true}
                                                            onClick={() => this.setState({ dialogConf: null })}
                                                        />,
                                                        <FlatButton
                                                            label="Save"
                                                            primary={true}
                                                            onClick={() => {
                                                                l.FlagIcon = this.state.selectedFlag;
                                                                this.localization.SetLanguageInfo(l);
                                                                this.setState({ dialogConf: null });
                                                            }}
                                                        />
                                                    ],
                                                    bodyFunc: (w, h) => <SVGIconSelector
                                                        width={w}
                                                        height={h}
                                                        defaultIcon={l.FlagIcon}
                                                        packages={["flags"]}
                                                        onIconSelection={(uri) => this.setState({ selectedFlag: uri })}
                                                    />
                                                }
                                            })
                                        }}
                                    >
                                        <YodiwoSVG uri={l.FlagIcon} width={35} height={35} />
                                    </div>
                                </TableRowColumn>
                                <TableRowColumn colSpan={1}>
                                    <Checkbox
                                        id={"isEnable-" + i}
                                        key={"isEnable-" + i}
                                        style={{
                                            width: "0px",
                                            margin: "0 auto"
                                        }}
                                        disabled={l.Code == this.localization.DefaultLanguage}
                                        checked={l.IsEnabled}
                                        onCheck={(event, isInputChecked) => {
                                            l.IsEnabled = isInputChecked;
                                            this.localization.SetLanguageInfo(l);
                                            this.forceUpdate();
                                        }}
                                    />
                                </TableRowColumn>
                                <TableRowColumn colSpan={1}>
                                    <Checkbox
                                        id={"isDefault-" + i}
                                        key={"isDefault-" + i}
                                        style={{
                                            width: "0px",
                                            margin: "0 auto"
                                        }}
                                        checkedIcon={<RadioButtonChecked />}
                                        uncheckedIcon={<RadioButtonUnChecked />}
                                        checked={l.IsDefault}
                                        onCheck={(event, isInputChecked) => {
                                            // Disable all other languages 
                                            let locInfo = this.localization.GetSupportedLanguages();
                                            for (var info in locInfo) {
                                                locInfo[info].IsDefault = false;
                                                this.localization.SetLanguageInfo(locInfo[info]);
                                            }

                                            // Set the default for the one that we have press
                                            l.IsDefault = true;
                                            this.localization.SetLanguageInfo(l);
                                            this.forceUpdate();
                                        }
                                        }
                                    />
                                </TableRowColumn>
                                <TableRowColumn colSpan={1}>
                                    <IconButton
                                        key={"Settings-Elements-Delete-" + i}
                                        touch={true}
                                        disabled={l.Code == this.localization.DefaultLanguage}
                                        onClick={() => {
                                            this.localization.DeleteLanguage(l.Code);
                                            this.forceUpdate();
                                        }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableRowColumn>
                            </TableRow>
                        );
                    })
                }
            </TableBody>
        </Table>
    }
    // -------------------------------------------------------------------------------------------
    renderTranslation(height: number) {
        const widgetsKey = this.localization.GetLanguageGroups(this.localization.DefaultLanguage);
        const groups = _.groupBy(widgetsKey, (k) => {
            let info = Localization.TryGetPageInfo(k);
            if (isDefined(info))
                return info.Name;
            return _.startsWith(k, "Primitive") ? "Primitive" : k.substr(0, 11)
        });
        const info = this.localization.GetSupportedLanguages();
        const infoKeys = _.filter(_.keys(info), (k) => k != this.localization.DefaultLanguage);
        if (infoKeys.length == 0)
            return null;

        const infoDefaultLang = info[this.localization.DefaultLanguage];
        let infoLang = info[this.state.selectedLanguageCode];
        if (!isDefined(infoLang)) {
            this.setState({ selectedLanguageCode: infoKeys[0] });
            infoLang = info[infoKeys[0]];
        }



        const style = {
            marginRight: 10,
            display: "inline-block"
        }
        return <div style={{ height: height, overflowY: "auto" }}>
            <Table>
                <TableHeader
                    displaySelectAll={false}
                    adjustForCheckbox={false}
                    enableSelectAll={false}>
                    <TableRow>
                        <TableHeaderColumn><div style={style}>{<YodiwoSVG uri={infoDefaultLang.FlagIcon} width={20} height={20} />}</div>{infoDefaultLang.Label + " (" + infoDefaultLang.Code + ")"}</TableHeaderColumn>
                        <TableHeaderColumn><div style={style}>{<YodiwoSVG uri={infoLang.FlagIcon} width={20} height={20} />}</div>{infoLang.Label + " (" + infoLang.Code + ")"}</TableHeaderColumn>
                    </TableRow>
                </TableHeader>
                <TableBody
                    displayRowCheckbox={false}
                    showRowHover={false}
                    stripedRows={false}>
                </TableBody>
            </Table>
            {
                // todo/question: add search in groups?
                _.map(groups, (g, gkey) => {
                    if (g.length == 0)
                        return null;

                    // todo/question: add search in group's items?
                    let gItems = _.map(g, (groupKey, i) => {
                        let textDefaultMap = this.localization.GetSpecificTextMap(infoDefaultLang.Code, groupKey, false);
                        let textTransMap = this.localization.GetSpecificTextMap(infoLang.Code, groupKey, (infoLang.Code == LocalizationDefaultKey) ? true : false);
                        let count = 0;
                        let subCategoryName = groupKey;
                        if (groupKey == "Primitive_DashboardManager")
                            subCategoryName = "Page List"
                        // todo/question: add subpage info!
                        return <Card style={{ padding: 0 }} key={groupKey + "-" + i}>
                            <CardHeader
                                subtitleStyle={{ color: "white" }}
                                titleStyle={{ color: "white" }}
                                iconStyle={{ color: "white" }}
                                style={{ backgroundColor: "rgb(91, 97, 99)" }}
                                title={subCategoryName}
                                subtitle={groupKey}
                                actAsExpander={true}
                                showExpandableButton={true}
                            />
                            <CardText style={{ padding: 0 }} expandable={true}>
                                <Table>
                                    <TableBody
                                        displayRowCheckbox={false}
                                        showRowHover={false}
                                        stripedRows={false}>
                                        {
                                            _.map(textDefaultMap, (text, key) => {
                                                count++;
                                                return <TableRow hoverable={true} striped={count % 2 == 0}>
                                                    <TableRowColumn>{text}</TableRowColumn>
                                                    <TableRowColumn>
                                                        <TextField
                                                            fullWidth
                                                            floatingLabelText={text}
                                                            floatingLabelFixed={true}
                                                            value={textTransMap[key] ? textTransMap[key] : ""}
                                                            onChange={(event) => {
                                                                let value = (event.target as any).value;
                                                                this.localization.AddTranslateText(infoLang.Code, groupKey, key, value);
                                                                this.forceUpdate();
                                                            }}
                                                        />
                                                    </TableRowColumn>
                                                </TableRow>
                                            })
                                        }
                                    </TableBody>
                                </Table>
                            </CardText>
                        </Card>
                    })

                    // TODO: get info for page
                    let pageInfo = this.props.PagesInfo[gkey];
                    if (!isDefined(pageInfo)) {
                        // get page info from `Localization`
                        pageInfo = Localization.TryGetPageInfo(gkey);
                        if (!isDefined(pageInfo))
                            // fallback; just add a dummy description
                            pageInfo = { Name: "", key: gkey };
                    }

                    return <Card style={{ padding: 0 }}>
                        <CardHeader
                            subtitleStyle={{ color: "white" }}
                            titleStyle={{ color: "white" }}
                            iconStyle={{ color: "white" }}
                            style={{ backgroundColor: "rgb(91, 97, 99)" }}
                            title={pageInfo.Name}
                            subtitle={pageInfo.key}
                            actAsExpander={true}
                            showExpandableButton={true}
                        />
                        <CardText style={{ padding: "0 0 0 16px" }} expandable={true}>
                            <Table>
                                <TableBody
                                    displayRowCheckbox={false}
                                    showRowHover={false}
                                    stripedRows={false}>
                                    {gItems}
                                </TableBody>
                            </Table>
                        </CardText>
                    </Card>
                })
            }
        </div>
    }
    // -------------------------------------------------------------------------------------------
    private dialogOnRefuse = () => {
        if (isFunction(this.props.onCloseRequest))
            this.props.onCloseRequest();
    }
    // -------------------------------------------------------------------------------------------
    private dialogOnAccept = () => {
        try {
            const origLangSystem = Localization.GetInstance(this.props.NodeKey);
            const infoOrig = origLangSystem.GetSupportedLanguages();
            const info = this.localization.GetSupportedLanguages();

            // Find deleted languages and delete them from the global system
            _.forOwn(infoOrig, (o, key) => {
                if (!_.has(info, key)) {
                    origLangSystem.DeleteLanguage(key);
                }
            });

            // Update default language info
            origLangSystem.SetLanguageInfo(info[this.localization.DefaultLanguage]);

            const infoKeys = _.keys(info);
            if (infoKeys.length > 0) {
                // Update original system with updated language
                _.forEach(infoKeys, (k) => {
                    origLangSystem.UpdateTranslatedLanguage(k, this.localization.GetLanguageDictionary(k));
                });
            }

            // Set the flag that we have pending data
            origLangSystem.PendingSaveChanges = true;

            if (isFunction(this.props.onSaveRequest))
                this.props.onSaveRequest();
        } catch (ex) {
            DebugEx.TraceCriticalException(ex);
        }
    }
    // -------------------------------------------------------------------------------------------
    render() {
        const tabHeight = 40;
        const { toolbarHeight } = this.props;
        let height = this.props.height - toolbarHeight - tabHeight;
        let transLangs = this.localization.GetSupportedLanguages(); //_.filter(this.localization.GetSupportedLanguages(), (l) => l.Code != this.localization.DefaultLanguage);
        return <div style={{ width: this.props.width, height: this.props.height }}>
            <Toolbar key="title-toolbar" style={{ boxShadow: "rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px", position: "relative", color: "white", backgroundColor: "#134459", zIndex: 1000 }} >
                <ToolbarGroup>
                    <ToolbarTitle style={{ color: "white" }} key="title-toolbar-title" text="Localization Settings" />
                </ToolbarGroup>
                <ToolbarGroup style={{ color: "white" }} lastChild={true}>
                    <ToolbarIconButton tooltip="Cancel" className="fas fa-times-circle dialog-button" onClick={this.dialogOnRefuse} />
                    <ToolbarIconButton tooltip="Save" className="fas fa-upload dialog-button" onClick={this.dialogOnAccept} />
                </ToolbarGroup>
            </Toolbar>
            <Tabs
                onChange={(value) => { this.setState({ tabIndex: value }) }}
                value={this.state.tabIndex}
                style={{
                    height: tabHeight, zIndex: 2, position: "relative",
                    boxShadow: "rgba(0, 0, 0, 0.16) 0px 3px 10px, rgba(0, 0, 0, 0.23) 0px 3px 10px"
                }}
                tabItemContainerStyle={{ height: tabHeight, backgroundColor: "rgb(91, 97, 99)" }}
            >
                <Tab label="Settings" value={0} />
                <Tab label="Translation" value={1} />
            </Tabs>
            <SwipeableViews
                index={this.state.tabIndex}
                onChangeIndex={(value) => { this.setState({ tabIndex: value }) }}
                style={{ width: this.props.width, height: height, overflowY: "auto", overflowX: "hidden" }}
            >
                <div>
                    <Toolbar style={{ height: tabHeight, backgroundColor: "rgb(0, 157, 119)" }}>
                        <ToolbarGroup>                        </ToolbarGroup>
                        <ToolbarGroup>
                            <IconButton tooltip='Add' tooltipPosition='bottom-left' touch={true} onClick={() => {
                                this.setState({
                                    dialogConf: {
                                        title: "Language Code",
                                        contentStaticSize: { w: 0.5, h: 0.3 },
                                        actions: [
                                            <FlatButton
                                                label="Close"
                                                secondary={true}
                                                onClick={() => this.setState({ dialogConf: null })}
                                            />,
                                            <FlatButton
                                                label="Add"
                                                primary={true}
                                                onClick={() => {
                                                    this.localization.SetLanguageInfo({
                                                        Code: this.state.newLanguageCode,
                                                        Label: this.state.newLanguageCode,
                                                        FlagIcon: "",
                                                        IsEnabled: true,
                                                        IsDefault: false
                                                    });
                                                    this.setState({ dialogConf: null });
                                                }}
                                            />
                                        ],
                                        bodyFunc: (w, h) => <div style={{ width: w, height: h, padding: 20 }}>
                                            <TextField
                                                fullWidth
                                                floatingLabelText="Code"
                                                floatingLabelFixed={true}
                                                onChange={(event) => {
                                                    let value = (event.target as any).value;
                                                    this.setState({ newLanguageCode: value });
                                                }}
                                            />
                                        </div>
                                    }
                                })
                            }}>
                                <AddIcon color="white" />
                            </IconButton>
                        </ToolbarGroup>
                    </Toolbar>
                    {this.renderLanguageTable()}
                </div>

                <div>
                    <Toolbar style={{ height: tabHeight, backgroundColor: "rgb(0, 157, 119)" }}>
                        <ToolbarGroup></ToolbarGroup>
                        <ToolbarGroup>
                            <DropDownMenu
                                style={{ color: "white", height: tabHeight, marginRight: 0 }}
                                labelStyle={{ color: "white", height: tabHeight, lineHeight: tabHeight + "px", paddingRight: 20 }}
                                iconStyle={{ right: 0, top: -4, width: 30, height: tabHeight }}
                                value={this.state.selectedLanguageCode}
                                onChange={(event, index, value) => {
                                    this.setState({ selectedLanguageCode: value })
                                }}>
                                {
                                    _.map(transLangs, (l) => <MenuItem value={l.Code} primaryText={l.Label + " (" + l.Code + ")"} leftIcon={<YodiwoSVG uri={l.FlagIcon} width={35} height={35} />} />)
                                }
                            </DropDownMenu>
                        </ToolbarGroup>
                    </Toolbar>
                    {this.renderTranslation(height - tabHeight)}
                </div>
            </SwipeableViews>

            <ExDialog
                key="dialog"
                Enable={this.state.dialogConf != null}
                Conf={this.state.dialogConf}
            />
        </div>;
    }
    // -------------------------------------------------------------------------------------------
}
// ================================================================================================
